import { Fragment, useMemo, useState, useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useSuspenseQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "@/components/admin/PageHeader";
import { RequirePermission } from "@/components/admin/RequirePermission";
import {
  deliveryPreviewFn,
  emailActivityLogFn,
  sendTestEmailFn,
  deleteEmailLogsFn,
  generateTestOrderEmailFn,
} from "@/lib/campaigns/campaigns.functions";

export const Route = createFileRoute("/admin/email-diagnostics")({
  head: () => ({
    meta: [
      { title: "Email Notification Logs — CB16 Console" },
      {
        name: "description",
        content: "View transactional emails sent to customers, preview messages, and resend when required.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: () => (
    <RequirePermission perm="campaigns.read">
      <EmailLogsPage />
    </RequirePermission>
  ),
});

const CARD = "border border-black/10 bg-white p-5";
const LABEL = "text-[10px] font-semibold uppercase tracking-[0.2em] text-black/45";

function Pill({ ok, label, tone }: { ok: boolean; label: string; tone?: "warn" }) {
  const normalized = label.toUpperCase();
  return (
    <span
      className={
        "inline-block px-2 py-0.5 text-[10px] font-semibold tracking-[0.18em] " +
        (tone === "warn" || normalized === "PENDING" || normalized === "QUEUED"
          ? "bg-amber-50 text-amber-700"
          : ok || normalized === "SENT" || normalized === "DELIVERED"
            ? "bg-emerald-50 text-emerald-700"
            : "bg-red-50 text-red-700")
      }
    >
      {normalized}
    </span>
  );
}

function fmt(v: string) {
  return v ? new Date(v).toLocaleString("en-IN") : "—";
}

function EmailLogsPage() {
  const queryClient = useQueryClient();
  const [autoRefresh, setAutoRefresh] = useState(true);
  const log = useSuspenseQuery({ 
    queryKey: ["email", "activity"], 
    queryFn: () => emailActivityLogFn(),
    staleTime: 0,
    gcTime: 1000 * 60 * 5, // 5 mins
    refetchInterval: autoRefresh ? 30000 : false,
  });
  
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const [expanded, setExpanded] = useState<string | null>(null);
  const [resending, setResending] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  
  // Resend form state
  const [resendTo, setResendTo] = useState("");
  const [resendSubject, setResendSubject] = useState("");
  const [resendBody, setResendBody] = useState("");

  const preview = useMutation({ mutationFn: (id: string) => deliveryPreviewFn({ data: { id } }) });
  
  const sendResend = useMutation({
    mutationFn: async (data: { to: string; subject: string; content: string }) => {
      return sendTestEmailFn({
        data: {
          to: data.to,
          from: "orders@cb16.in",
          campaign: {
            subject: data.subject,
            content: data.content,
            type: "custom", // Resends use custom to allow edits
            template: "luxury",
            previewText: data.subject,
            ctaLabel: "View Order",
            ctaUrl: "https://cb16.in/account/orders",
            audience: "test",
            name: "Resend Notification",
            audienceIds: [],
            audienceTags: [],
            audienceEmails: [],
            featuredImage: "",
            scheduledAt: "",
          }
        }
      });
    },
    onSuccess: () => {
      setResending(null);
      // Invalidate both to ensure new log entry appears
      queryClient.invalidateQueries({ queryKey: ["email", "activity"] });
    }
  });

  const deleteLogs = useMutation({
    mutationFn: (ids: string[]) => deleteEmailLogsFn({ data: { ids } }),
    onSuccess: () => {
      setSelectedIds(new Set());
      queryClient.invalidateQueries({ queryKey: ["email", "activity"] });
    }
  });

  const generateTestEmail = useMutation({
    mutationFn: () => generateTestOrderEmailFn(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["email", "activity"] });
    }
  });

  const rows = useMemo(() => {
    const data = log.data ?? [];
    return data.filter((r) => {
      const matchSearch = !search || 
        r.email.toLowerCase().includes(search.toLowerCase()) ||
        r.orderNumber.toLowerCase().includes(search.toLowerCase()) ||
        r.subject.toLowerCase().includes(search.toLowerCase());
      
      const matchStatus = !status || r.status.toLowerCase() === status.toLowerCase();
      const matchType = !type || r.notificationType.toLowerCase() === type.toLowerCase();
      
      return matchSearch && matchStatus && matchType;
    });
  }, [log.data, search, status, type]);

  const toggleSelectAll = () => {
    if (selectedIds.size === rows.length && rows.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(rows.map(r => r.id)));
    }
  };

  const toggleSelect = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const handleBulkDelete = () => {
    if (!selectedIds.size) return;
    if (confirm(`Delete ${selectedIds.size} email log records? This cannot be undone.`)) {
      deleteLogs.mutate(Array.from(selectedIds));
    }
  };

  const handleDelete = (id: string) => {
    if (confirm("Delete this email log record?")) {
      deleteLogs.mutate([id]);
    }
  };

  const uniqueTypes = useMemo(() => {
    const data = log.data ?? [];
    return Array.from(new Set(data.map(r => r.notificationType))).filter(Boolean).sort();
  }, [log.data]);

  return (
    <div className="pb-20">
      <div className="flex justify-between items-start mb-6">
        <PageHeader
          title="Email Notification Logs"
          subtitle="Audit communications, preview templates, and send follow-ups."
        />
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-black/5 rounded-sm">
            <div className={`w-2 h-2 rounded-full ${autoRefresh ? 'bg-emerald-500 animate-pulse' : 'bg-black/20'}`} />
            <span className="text-[10px] uppercase tracking-wider text-black/60">Auto-refresh: 30s</span>
            <input 
              type="checkbox" 
              checked={autoRefresh} 
              onChange={e => setAutoRefresh(e.target.checked)}
              className="ml-1 cursor-pointer"
            />
          </div>
          <button
            type="button"
            disabled={generateTestEmail.isPending}
            onClick={() => generateTestEmail.mutate()}
            className="bg-emerald-700 text-white px-4 py-2 text-[10px] uppercase tracking-[0.12em] font-medium hover:bg-emerald-800 transition-colors h-[38px] flex items-center gap-2"
          >
            {generateTestEmail.isPending ? "Generating..." : "Generate Test Order Email"}
          </button>
          <button
            type="button"
            onClick={() => queryClient.invalidateQueries({ queryKey: ["email", "activity"] })}
            className="border border-black/15 px-4 py-2 text-[10px] uppercase tracking-[0.12em] font-medium hover:bg-black/5 transition-colors h-[38px] flex items-center gap-2"
          >
            <svg className={`w-3 h-3 ${log.isFetching ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      <div className={CARD + " mb-6"}>
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[200px] relative">
            <label className="block text-[10px] uppercase tracking-wider text-black/50 mb-1">Search</label>
            <div className="relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-black/30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input 
                type="text"
                placeholder="Search email, subject, or order #"
                className="w-full border border-black/10 pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-black/30"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-black/50 mb-1">Status</label>
            <select
              className="border border-black/10 px-3 py-2 text-sm min-w-[140px] focus:outline-none focus:border-black/30"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="sent">Sent</option>
              <option value="failed">Failed</option>
              <option value="queued">Queued</option>
              <option value="pending">Pending</option>
              <option value="skipped">Skipped</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-black/50 mb-1">Type</label>
            <select
              className="border border-black/10 px-3 py-2 text-sm min-w-[160px] focus:outline-none focus:border-black/30"
              value={type}
              onChange={(e) => setType(e.target.value)}
            >
              <option value="">All Types</option>
              {uniqueTypes.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {selectedIds.size > 0 && (
        <div className="mb-4 flex items-center justify-between bg-black text-white px-4 py-2 text-[10px] uppercase tracking-[0.2em] font-medium">
          <span>{selectedIds.size} records selected</span>
          <button 
            onClick={handleBulkDelete}
            className="flex items-center gap-2 hover:text-red-400 transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Bulk Delete
          </button>
        </div>
      )}

      <div className={CARD}>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1000px] text-left text-[12px]">
            <thead className="border-b border-black/10 text-[10px] uppercase tracking-[0.18em] text-black/50">
              <tr>
                <th className="py-2 pr-3 w-8">
                  <input 
                    type="checkbox" 
                    className="cursor-pointer" 
                    checked={rows.length > 0 && selectedIds.size === rows.length}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th className="py-2 pr-3">Notification</th>
                <th className="py-2 pr-3">Customer</th>
                <th className="py-2 pr-3">Order #</th>
                <th className="py-2 pr-3">Date/Time</th>
                <th className="py-2 pr-3 text-center">Provider</th>
                <th className="py-2 pr-3 text-center">Status</th>
                <th className="py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-black/50">
                    No matching email activity records found.
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <Fragment key={r.id}>
                  <tr className={`border-b border-black/5 align-middle hover:bg-black/[0.01] ${selectedIds.has(r.id) ? 'bg-black/[0.02]' : ''}`}>
                    <td className="py-4 pr-3">
                      <input 
                        type="checkbox" 
                        className="cursor-pointer"
                        checked={selectedIds.has(r.id)}
                        onChange={() => toggleSelect(r.id)}
                      />
                    </td>
                    <td className="py-4 pr-3">
                      <div className="font-semibold text-black">{r.notificationType}</div>
                      <div className="text-[10px] text-black/40 truncate max-w-[200px]" title={r.subject}>
                        {r.subject}
                      </div>
                    </td>
                    <td className="py-4 pr-3">{r.email}</td>
                    <td className="py-4 pr-3 font-mono">{r.orderNumber}</td>
                    <td className="py-4 pr-3 whitespace-nowrap text-black/60">{fmt(r.at)}</td>
                    <td className="py-4 pr-3 text-center">
                      <span className="text-[10px] font-medium px-2 py-0.5 border border-black/10 rounded-sm">
                        {r.provider}
                      </span>
                    </td>
                    <td className="py-4 pr-3 text-center">
                      <Pill ok={r.status === "sent"} label={r.status} />
                    </td>
                    <td className="py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          className="px-2 py-1 text-[10px] uppercase tracking-wider text-black/60 hover:text-black border border-black/10 hover:border-black/20"
                          onClick={() => {
                            setExpanded(expanded === r.id ? null : r.id);
                            setResending(null);
                            if (expanded !== r.id) preview.mutate(r.id);
                          }}
                        >
                          {expanded === r.id ? "Close" : "Preview"}
                        </button>
                        <button
                          type="button"
                          className="px-2 py-1 text-[10px] uppercase tracking-wider bg-black text-white hover:bg-black/80"
                          onClick={() => {
                            setResending(resending === r.id ? null : r.id);
                            setExpanded(null);
                            setResendTo(r.email);
                            setResendSubject(r.subject);
                            // Set content from raw data if available immediately, otherwise it loads on preview
                            setResendBody(r.html || r.text || "");
                          }}
                        >
                          Resend
                        </button>
                        <button
                          type="button"
                          className="p-1.5 text-black/40 hover:text-red-600 transition-colors"
                          onClick={() => handleDelete(r.id)}
                          title="Delete Log"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                  
                  {expanded === r.id && (
                    <tr>
                      <td colSpan={8} className="bg-black/[0.02] p-6 border-b border-black/5">
                        <div className="max-w-[800px] mx-auto">
                          {preview.isPending && <p className="text-black/50 text-center py-8">Loading rendered email...</p>}
                          {preview.data && preview.data.id === r.id ? (
                            <div>
                              <div className="mb-4 grid grid-cols-2 gap-4 text-[11px] border-b border-black/10 pb-4">
                                <div>
                                  <span className="text-black/40 uppercase tracking-tighter mr-2">Subject:</span>
                                  <span className="text-black font-medium">{preview.data.subject}</span>
                                </div>
                                <div className="text-right">
                                  <span className="text-black/40 uppercase tracking-tighter mr-2">Provider ID:</span>
                                  <span className="font-mono">{r.providerId || "—"}</span>
                                </div>
                              </div>
                              {preview.data.html ? (
                                <div className="bg-white border border-black/10 shadow-sm overflow-hidden">
                                  <iframe
                                    title="Email Preview"
                                    srcDoc={preview.data.html}
                                    className="h-[600px] w-full"
                                  />
                                </div>
                              ) : (
                                <div className="bg-white p-8 border border-black/10 text-black/50 text-center">
                                  No visual copy available.
                                  {preview.data.text && (
                                    <pre className="mt-4 text-left font-sans text-xs whitespace-pre-wrap text-black">
                                      {preview.data.text}
                                    </pre>
                                  )}
                                </div>
                              )}
                            </div>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  )}

                  {resending === r.id && (
                    <tr>
                      <td colSpan={8} className="bg-[#C6A15B]/5 p-6 border-b border-black/5">
                        <div className="max-w-[800px] mx-auto">
                          <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] mb-4 text-[#C6A15B]">Resend Notification</h3>
                          <div className="grid gap-4 bg-white p-6 border border-[#C6A15B]/20 shadow-sm">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label className="block text-[10px] uppercase tracking-wider text-black/40 mb-1">From</label>
                                <input 
                                  type="text" 
                                  disabled 
                                  className="w-full bg-black/5 border border-black/10 px-3 py-2 text-sm text-black/50 cursor-not-allowed"
                                  value="orders@cb16.in"
                                />
                              </div>
                              <div>
                                <label className="block text-[10px] uppercase tracking-wider text-black/40 mb-1">To</label>
                                <input 
                                  type="email" 
                                  className="w-full border border-black/10 px-3 py-2 text-sm"
                                  value={resendTo}
                                  onChange={e => setResendTo(e.target.value)}
                                />
                              </div>
                            </div>
                            <div>
                              <label className="block text-[10px] uppercase tracking-wider text-black/40 mb-1">Subject</label>
                              <input 
                                type="text" 
                                className="w-full border border-black/10 px-3 py-2 text-sm"
                                value={resendSubject}
                                onChange={e => setResendSubject(e.target.value)}
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] uppercase tracking-wider text-black/40 mb-1">Message Body (HTML)</label>
                              <textarea 
                                className="w-full border border-black/10 px-3 py-2 text-sm font-mono h-[300px]"
                                value={resendBody}
                                onChange={e => setResendBody(e.target.value)}
                                placeholder="Loading original content..."
                              />
                              <p className="mt-1 text-[10px] text-black/40 italic">Note: Editing content here will send as a custom notification.</p>
                            </div>
                            <div className="flex justify-end gap-3 pt-2">
                              <button
                                type="button"
                                className="px-6 py-2 text-[10px] uppercase tracking-[0.2em] font-medium border border-black/10 hover:bg-black/5"
                                onClick={() => setResending(null)}
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                disabled={sendResend.isPending || !resendTo.includes("@")}
                                className="px-8 py-2 text-[10px] uppercase tracking-[0.2em] font-medium bg-black text-white disabled:opacity-50"
                                onClick={() => sendResend.mutate({
                                  to: resendTo,
                                  subject: resendSubject,
                                  content: resendBody
                                })}
                              >
                                {sendResend.isPending ? "Sending..." : "Send Now"}
                              </button>
                            </div>
                            {sendResend.isError && (
                              <p className="mt-2 text-xs text-red-700 font-medium">Error: {sendResend.error instanceof Error ? sendResend.error.message : "Failed to send"}</p>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
